import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js';
import HelpVideoModal from '../../modules/utils/HelpVideoModal'
import {useModule} from '../../platform/modules'

ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const API_URL = process.env.REACT_APP_API_URL;

/** Basit KPI kartı */
const KpiCard = ({ title, value, sub }) => (
    <div className="col-md-3 mb-3">
        <div className="card h-100">
            <div className="card-body">
                <div className="text-muted small">{title}</div>
                <div className="fs-4 fw-semibold">{value ?? '-'}</div>
                {sub ? <div className="text-muted">{sub}</div> : null}
            </div>
        </div>
    </div>
);

/** Bar grafiği: iki veri kümesi (opsiyonel) */
const SimpleBar = ({ labels, series, seriesLabels }) => {
    const data = {
        labels,
        datasets: series.map((arr, i) => ({
            label: seriesLabels?.[i] ?? `Series ${i + 1}`,
            data: arr,
            borderWidth: 1,
        })),
    };
    const options = {
        responsive: true,
        plugins: { legend: { position: 'top' } },
        scales: {
            y: { beginAtZero: true },
        },
    };
    return <Bar data={data} options={options} />;
};

/** Line grafiği: tek seri */
const SimpleLine = ({ labels, series, label }) => {
    const data = {
        labels,
        datasets: [
            {
                label: label ?? 'Veri',
                data: series,
                tension: 0.25,
            },
        ],
    };
    const options = {
        responsive: true,
        plugins: { legend: { position: 'top' } },
        scales: { y: { beginAtZero: true } },
    };
    return <Line data={data} options={options} />;
};

const fmtDate = (d) => {
    if (!d) return '';
    // ISO string gelebilir; güvenli şekilde date-only yazalım:
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString();
};

const toYMD = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    // ISO benzeri string ise ilk 10 karakter genelde YYYY-MM-DD’dir:
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    // Parse edilebiliyorsa deneyelim, aksi halde son çare slice(0,10)
    const t = Date.parse(s);
    if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
    return s.slice(0, 10);
};
const fmtDateOnly = (d) => {
    if (!d) return '-';
    const t = Date.parse(d);
    if (Number.isNaN(t)) return '-';
    return new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtDateTime = (d) => {
    if (!d) return '-';
    const t = Date.parse(d);
    if (Number.isNaN(t)) return '-';
    return new Date(t).toLocaleString('tr-TR');
};


const ExamPeriodSummaryDashboard = () => {
    const {defaultRoute} = useModule()
    const navigate = useNavigate()
    const [periods, setPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [summary, setSummary] = useState(null);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [error, setError] = useState('');
    // üst kısımda:
    const [issuesModalOpen, setIssuesModalOpen] = useState(false);
    const [issuesLoading, setIssuesLoading] = useState(false);
    const [issues, setIssues] = useState([]);
    const [issuesDateFilter, setIssuesDateFilter] = useState(''); // YYYY-MM-DD ya da ''

    const [startModalOpen, setStartModalOpen] = useState(false);
    const [startReportsLoading, setStartReportsLoading] = useState(false);
    const [startReportsDetail, setStartReportsDetail] = useState([]);
    const [startDateFilter, setStartDateFilter] = useState('');

    const loadIssues = (periodId, dateStr) => {
        if (!periodId) return;
        setIssuesLoading(true);
        let url = `${API_URL}/Custom/ExamIssuesList?periodId=${periodId}`;
        if (dateStr) url += `&date=${dateStr}`;
        axios.get(url)
            .then(res => setIssues(res.data ?? []))
            .catch(() => setIssues([]))
            .finally(() => setIssuesLoading(false));
    };

    const loadStartReports = (periodId, dateStr) => {
        if (!periodId) return;
        setStartReportsLoading(true);
        let url = `${API_URL}/Custom/ExamStartReportsList?periodId=${periodId}`;
        if (dateStr) url += `&date=${dateStr}`;
        axios.get(url)
            .then(res => setStartReportsDetail(res.data ?? []))
            .catch(() => setStartReportsDetail([]))
            .finally(() => setStartReportsLoading(false));
    };

    // Dönemleri çek
    useEffect(() => {
        setLoadingPeriods(true);
        axios
            .get(`${API_URL}/Custom/ListPublishedExamPeriods`)
            .then((res) => setPeriods(res.data ?? []))
            .catch(() => setPeriods([]))
            .finally(() => setLoadingPeriods(false));
    }, []);

    // Özet verisini çek
    useEffect(() => {
        if (!selectedPeriod) {
            setSummary(null);
            return;
        }
        setLoadingSummary(true);
        setError('');
        axios
            .get(`${API_URL}/Custom/ExamPeriodSummary?periodId=${selectedPeriod}`)
            .then((res) => setSummary(res.data))
            .catch((e) => {
                setSummary(null);
                setError('Özet verisi getirilemedi.');
            })
            .finally(() => setLoadingSummary(false));
    }, [selectedPeriod]);

    // Chart verileri hazırlıkları (null guard)
    const sessionsPerDay = summary?.charts?.sessionsPerDay ?? [];
    const seatsPerDay = summary?.charts?.seatsPerDay ?? [];
    const startPerDay = summary?.startReports?.perDay ?? [];
    const issuesPerDay = summary?.issues?.perDay ?? [];
    const startByUser = summary?.startReports?.byUser ?? [];

    const sessionsLabels = sessionsPerDay.map((x) => fmtDate(x.date));
    const sessionsSessionCount = sessionsPerDay.map((x) => x.sessionCount);
    const sessionsClassroomCount = sessionsPerDay.map((x) => x.classroomCount);
    const seatsLabels = seatsPerDay.map((x) => fmtDate(x.date));
    const seatsCounts = seatsPerDay.map((x) => x.seatCount);

    const startLabels = startPerDay.map((x) => fmtDate(x.date));
    const startOk = startPerDay.map((x) => x.ok);
    const startNotOk = startPerDay.map((x) => x.notOk);
    const startDatesRaw = startPerDay.map((x) => x.date);

    const issuesLabels = issuesPerDay.map((x) => fmtDate(x.date));
    const issuesCounts = issuesPerDay.map((x) => x.count);
    const issuesDatesRaw = issuesPerDay.map((x) => x.date); // ISO/Date string ham değer


    return (
        <div className="container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="btn btn-light" onClick={() => navigate(defaultRoute)}>
                    🔙 Geri
                </button>

                <HelpVideoModal videoName="SinavSureci" />
            </div>
            <h3 className="mb-4">Sınav Dönemi Özeti</h3>

            {/* Dönem seçimi */}
            <div className="card p-3 mb-4">
                <div className="row align-items-end">
                    <div className="col-md-6">
                        <label className="form-label">Sınav Dönemi</label>
                        <select
                            className="form-select"
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            disabled={loadingPeriods}
                        >
                            <option value="">Seçiniz</option>
                            {periods.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name} {p.examTypeName ? `- ${p.examTypeName}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                </div>
                {loadingPeriods && (
                    <div className="text-muted mt-2">Dönemler yükleniyor...</div>
                )}

            </div>

            {/* Özet */}
            {loadingSummary ? (
                <div className="d-flex justify-content-center align-items-center my-5" style={{ minHeight: 200 }}>
                    <div className="spinner-border text-primary me-2" role="status" />
                    <span>Özet yükleniyor...</span>
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : summary ? (
                <>
                    {/* Üst Bilgi */}
                    <div className="card p-3 mb-4">
                        <div className="row">
                            <div className="col-md-4">
                                <div className="text-muted small">Dönem Adı</div>
                                <div className="fw-semibold">
                                    {summary.period?.name ?? '-'}
                                    {summary.period?.examTypeName ? ` (${summary.period.examTypeName})` : ''}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="text-muted small">Tarih Aralığı</div>
                                <div className="fw-semibold">
                                    {fmtDate(summary.period?.timeSlots?.dateRangeStart)} — {fmtDate(summary.period?.timeSlots?.dateRangeEnd)}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="text-muted small">Toplam Slot</div>
                                <div className="fw-semibold">{summary.period?.timeSlots?.total ?? '-'}</div>
                            </div>
                        </div>
                    </div>

                    {/* KPI Kartları */}
                    <div className="row">
                        <KpiCard
                            title="Oturum Yapılan Slot"
                            value={summary.sessions?.timeSlotWithExamCount}
                            sub="En az 1 sınav barındıran slot sayısı"
                        />
                        <KpiCard
                            title="Toplam Oturum"
                            value={summary.sessions?.examScheduleCount}
                            sub="Planlanan oturum sayısı"
                        />
                        <KpiCard
                            title="Kullanılan Sınıf"
                            value={summary.sessions?.classroomsUsed}
                        />
                        <KpiCard
                            title="Koltuk Ataması"
                            value={summary.sessions?.seatAssignmentsCount}
                            sub="Öğrenci-oturum ataması"
                        />

                        <KpiCard
                            title="Görevli Slot Adedi"
                            value={summary.staffing?.totalAssigned}
                            sub={`Benzersiz kişi: ${summary.staffing?.uniquePeopleAssigned ?? '-'}`}
                        />
                        <KpiCard
                            title="Onaylı Görev Değişimi"
                            value={summary.swaps?.approvedCount}
                            sub={`Toplam başvuru: ${summary.swaps?.total ?? '-'}`}
                        />
                        <KpiCard
                            title="BSS Başlatma Raporu"
                            value={`${summary.startReports?.ok ?? 0}`}
                            sub={`Toplam rapor: ${summary.startReports?.total ?? 0} | Sorunlu: ${summary.startReports?.notOk ?? 0}`}
                        />
                        <KpiCard
                            title="Sorun (Issue) Toplam"
                            value={summary.issues?.total}
                            sub={`Ekli Dosya: ${summary.issues?.withAttachment ?? 0}`}
                        />
                    </div>

                    <div className="card p-3 mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">BSS Sınav Başlatma Raporları (Personel)</h5>
                            {selectedPeriod && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => {
                                        setStartDateFilter('');
                                        setStartReportsDetail([]);
                                        setStartModalOpen(true);
                                        loadStartReports(selectedPeriod, '');
                                    }}
                                >
                                    Detaylar
                                </button>
                            )}
                        </div>
                        {startByUser.length ? (
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Personel</th>
                                            <th className="text-end">Toplam</th>
                                            <th className="text-end">OK</th>
                                            <th className="text-end">Sorunlu</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {startByUser.map((u) => (
                                            <tr key={u.userId}>
                                                <td>
                                                    <div className="fw-semibold">{u.name ?? '-'}</div>
                                                    <div className="small text-muted">#{u.userId}</div>
                                                </td>
                                                <td className="text-end">{u.total}</td>
                                                <td className="text-end">{u.ok}</td>
                                                <td className="text-end">{u.notOk}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-muted">Personel bazlı kayıt bulunamadı.</div>
                        )}
                    </div>

                    {/* Rol Bazlı Görevli Dağılımı */}
                    <div className="card p-3 mb-4">
                        <h5 className="mb-3">Görevli Dağılımı (Role Göre)</h5>
                        {summary.staffing?.byRole?.length ? (
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Rol</th>
                                            <th className="text-end">Adet</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.staffing.byRole.map((r) => (
                                            <tr key={r.roleId}>
                                                <td>{r.roleName}</td>
                                                <td className="text-end">{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-muted">Rol bazlı dağılım bulunamadı.</div>
                        )}
                    </div>

                    {/* Swap Dağılımı (Duruma Göre) */}
                    <div className="card p-3 mb-4">
                        <h5 className="mb-3">Görev Değişimi (Duruma Göre)</h5>
                        {summary.swaps?.byStatus?.length ? (
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Durum</th>
                                            <th className="text-end">Adet</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {summary.swaps.byStatus.map((s, i) => (
                                            <tr key={i}>
                                                <td>{s.statusName}</td>
                                                <td className="text-end">{s.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-muted">Kayıt bulunamadı.</div>
                        )}
                    </div>

                    {/* Günlük Oturum/Sınıf grafiği */}
                    <div className="card p-3 mb-4">
                        <h5 className="mb-3">Günlük Oturum / Sınıf Sayısı</h5>
                        {sessionsPerDay.length ? (
                            <SimpleBar
                                labels={sessionsLabels}
                                series={[sessionsSessionCount, sessionsClassroomCount]}
                                seriesLabels={['Oturum (Slot)', 'Sınıf']}
                            />
                        ) : (
                            <div className="text-muted">Veri yok.</div>
                        )}
                    </div>

                    {/* Günlük Koltuk Ataması grafiği */}
                    <div className="card p-3 mb-4">
                        <h5 className="mb-3">Günlük Koltuk Ataması</h5>
                        {seatsPerDay.length ? (
                            <SimpleLine labels={seatsLabels} series={seatsCounts} label="Koltuk" />
                        ) : (
                            <div className="text-muted">Veri yok.</div>
                        )}
                    </div>

                    {/* Başlangıç Raporları (OK/Değil) */}
                    <div className="card p-3 mb-4">
                        <h5 className="mb-3">BSS Sınav Başlatma Raporları (Günlük)</h5>
                        {startPerDay.length ? (
                            <SimpleBar
                                labels={startLabels}
                                series={[startOk, startNotOk]}
                                seriesLabels={['OK', 'Sorunlu']}
                            />
                        ) : (
                            <div className="text-muted">Veri yok.</div>
                        )}
                    </div>

                    {/* Günlük Issue grafiği */}
                    <div className="card p-3 mb-5">
                        <h5 className="mb-3">Günlük Problem Raporu Sayısı{selectedPeriod && (
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                        onClick={() => {
                                            setIssuesDateFilter('');          // tüm günler
                                            setIssuesModalOpen(true);
                                            loadIssues(selectedPeriod, '');   // date filtresi olmadan getir
                                        }}
                                        style={{ zIndex: 2 }}
                                    >
                                        Detaylar
                                    </button>
                                )}</h5>

                                <div className="position-relative">
                                    {/* Kartın İÇİNDE sağ üstte “Detaylar” */}
                                    

                                    {/* Grafik / içerik */}
                                    {issuesPerDay.length ? (
                                        <SimpleBar
                                            labels={issuesLabels}
                                            series={[issuesCounts]}
                                            seriesLabels={['Issue']}
                                        />
                                    ) : (
                                        <div className="text-muted">Veri yok.</div>
                                    )}
                                </div>
                            </div>
                            {startModalOpen && (
                                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                    <div className="modal-dialog modal-xl modal-dialog-centered">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Sınav Başlatma Raporu Detayları</h5>
                                                <button type="button" className="btn-close" onClick={() => setStartModalOpen(false)}></button>
                                            </div>
                                            <div className="modal-body">
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">Gün Filtresi</label>
                                                        <select
                                                            className="form-select"
                                                            value={startDateFilter}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setStartDateFilter(v);
                                                                loadStartReports(selectedPeriod, v);
                                                            }}
                                                        >
                                                            <option value="">Tümü</option>
                                                            {startPerDay.map((d, idx) => {
                                                                const raw = startDatesRaw[idx] ?? d.date;
                                                                const label = fmtDateOnly(raw);
                                                                const ymd = toYMD(raw);
                                                                return (
                                                                    <option key={ymd} value={ymd}>
                                                                        {label}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                </div>

                                                {startReportsLoading ? (
                                                    <div className="d-flex align-items-center">
                                                        <div className="spinner-border text-primary me-2" role="status" />
                                                        <span>Yükleniyor...</span>
                                                    </div>
                                                ) : startReportsDetail.length ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm align-middle">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ minWidth: 80 }}>Tarih / Slot</th>
                                                                    <th style={{ minWidth: 90 }}>Bina / Sınıf</th>
                                                                    <th style={{ minWidth: 90 }}>Durum</th>
                                                                    <th style={{ minWidth: 240 }}>Not</th>
                                                                    <th style={{ minWidth: 120 }}>Bildiren</th>
                                                                    <th style={{ minWidth: 120 }}>Oluşturma</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {startReportsDetail.map((it) => (
                                                                    <tr key={it.id}>
                                                                        <td>
                                                                            <div className="fw-semibold">{fmtDateOnly(it.exactDate)}</div>
                                                                            <div className="small text-muted">{it.timeSlotName ?? '-'}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="small text-muted">{it.buildingName ?? '-'}</div>
                                                                            <div className="fw-semibold">{it.classroomCode ?? '-'}</div>
                                                                        </td>
                                                                        <td>
                                                                            {it.startedOk ? (
                                                                                <span className="badge bg-success">OK</span>
                                                                            ) : (
                                                                                <span className="badge bg-danger">Sorunlu</span>
                                                                            )}
                                                                        </td>
                                                                        <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                            {it.note ? it.note : <span className="text-muted">-</span>}
                                                                        </td>
                                                                        <td>
                                                                            <div className="fw-semibold">{it.createdBy ?? '-'}</div>
                                                                            <div className="small text-muted">#{it.createdById}</div>
                                                                        </td>
                                                                        <td>{fmtDateTime(it.createdAt)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-info">Kayıt bulunamadı.</div>
                                                )}
                                            </div>
                                            <div className="modal-footer">
                                                <button type="button" className="btn btn-secondary" onClick={() => setStartModalOpen(false)}>Kapat</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {issuesModalOpen && (
                                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                    <div className="modal-dialog modal-xl modal-dialog-centered">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">Problem Raporu Detayları</h5>
                                                <button type="button" className="btn-close" onClick={() => setIssuesModalOpen(false)}></button>
                                            </div>
                                            <div className="modal-body">
                                                {/* Gün filtresi */}
                                                <div className="row mb-3">
                                                    <div className="col-md-6">
                                                        <label className="form-label">Gün Filtresi</label>
                                                        <select
                                                            className="form-select"
                                                            value={issuesDateFilter}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setIssuesDateFilter(v);
                                                                loadIssues(selectedPeriod, v); // YYYY-MM-DD gönderiyoruz
                                                            }}
                                                        >
                                                            <option value="">Tümü</option>
                                                            {issuesPerDay.map((d, idx) => {
                                                                const raw = issuesDatesRaw[idx];
                                                                const label = fmtDateOnly(raw);
                                                                const ymd = toYMD(raw);
                                                                return (
                                                                    <option key={ymd} value={ymd}>
                                                                        {label}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Liste */}
                                                {issuesLoading ? (
                                                    <div className="d-flex align-items-center">
                                                        <div className="spinner-border text-primary me-2" role="status" />
                                                        <span>Yükleniyor...</span>
                                                    </div>
                                                ) : issues.length ? (
                                                    <div className="table-responsive">
                                                        <table className="table table-sm align-middle">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ minWidth: 80 }}>Tarih / Slot</th>
                                                                    <th style={{ minWidth: 90 }}>Bina / Sınıf</th>
                                                                    <th style={{ minWidth: 200 }}>Görevliler</th>
                                                                    <th style={{ minWidth: 100 }}>Başlık</th>
                                                                    <th style={{ minWidth: 320 }}>Açıklama</th>
                                                                    <th>Ek</th>
                                                                    <th style={{ minWidth: 100 }}>Bildiren</th>
                                                                    <th style={{ minWidth: 100 }}>Oluşturma</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {issues.map((it) => (
                                                                    <tr key={it.id}>
                                                                        {/* Tarih + Slot adı */}
                                                                        <td>
                                                                            <div className="fw-semibold">{fmtDateOnly(it.exactDate)}</div>
                                                                            <div className="small text-muted">{it.timeSlotName || '-'}</div>
                                                                        </td>

                                                                        {/* Bina / Sınıf */}
                                                                        <td>
                                                                            <div className="small text-muted">{it.buildingName ?? '-'}</div>
                                                                            <div className="fw-semibold">{it.classroomCode ?? '-'}</div>
                                                                        </td>

                                                                        {/* Görevliler (Salon Başkanı + Gözetmenler) */}
                                                                        <td>
                                                                            <div className="mb-1">
                                                                                <span className="badge bg-dark me-2">S. Başkanı</span>
                                                                                {it.chief ? (
                                                                                    <span className="fw-semibold">{it.chief.name}</span>
                                                                                ) : (
                                                                                    <span className="text-muted">-</span>
                                                                                )}
                                                                            </div>
                                                                            <div>
                                                                                <span className="badge bg-danger me-2">Gözetmen</span>
                                                                                {it.invigilators && it.invigilators.length > 0 ? (
                                                                                    <span>
                                                                                        {it.invigilators.map((g, idx) => (
                                                                                            <span key={g.userId}>
                                                                                                {g.name}{idx < it.invigilators.length - 1 ? ', ' : ''}
                                                                                            </span>
                                                                                        ))}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-muted">-</span>
                                                                                )}
                                                                            </div>
                                                                        </td>

                                                                        {/* Başlık */}
                                                                        <td className="fw-semibold">{it.title}</td>

                                                                        {/* Açıklama (satır sarma) */}
                                                                        <td style={{ maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {it.description || <span className="text-muted">-</span>}
                                                                        </td>

                                                                        {/* Ek */}
                                                                        <td>
                                                                            {it.filePath ? (
                                                                                <a
                                                                                    href={
                                                                                        it.filePath.startsWith('http')
                                                                                            ? it.filePath
                                                                                            : `${API_URL?.replace(/\/api\/?$/, '')}${it.filePath.startsWith('/') ? '' : '/'}${it.filePath}`
                                                                                    }
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                >
                                                                                    İndir
                                                                                </a>
                                                                            ) : (
                                                                                <span className="text-muted">-</span>
                                                                            )}
                                                                        </td>

                                                                        {/* Bildiren */}
                                                                        <td>
                                                                            <div className="fw-semibold">{it.createdBy ?? '-'}</div>
                                                                            <div className="small text-muted">#{it.createdById}</div>
                                                                        </td>

                                                                        {/* Oluşturma */}
                                                                        <td>{fmtDateTime(it.createdAt)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-info">Kayıt bulunamadı.</div>
                                                )}
                                            </div>
                                            <div className="modal-footer">
                                                <button type="button" className="btn btn-secondary" onClick={() => setIssuesModalOpen(false)}>Kapat</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}


                </>
            ) : selectedPeriod ? (
                <div className="alert alert-warning">Veri bulunamadı.</div>
            ) : null}
        </div>
    );
};

export default ExamPeriodSummaryDashboard;
